import type { StorefrontAppearance } from "@/features/pos/storefront-appearance";
import axios, { AxiosInstance, AxiosResponse, AxiosError } from "axios";
import { authStorage } from "./auth-storage";

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api";

// Create axios instance
const api: AxiosInstance = axios.create({
	baseURL: API_BASE_URL,
	timeout: 10000,
	headers: {
		"Content-Type": "application/json",
	},
});

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
	const refreshToken = authStorage.getRefreshToken();
	if (!refreshToken) return null;

	try {
		// Use bare axios so a rejected refresh never re-enters this interceptor.
		const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
			refresh_token: refreshToken,
		});
		const accessToken = typeof response.data?.access_token === "string" ? response.data.access_token : "";
		if (!accessToken) return null;
		authStorage.setAccessToken(accessToken);
		return accessToken;
	} catch {
		return null;
	}
}

// Request interceptor to add auth token
api.interceptors.request.use(
	config => {
		const token = authStorage.getAccessToken();
		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}
		return config;
	},
	error => {
		return Promise.reject(error);
	},
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
	(response: AxiosResponse) => response,
	async (error: AxiosError) => {
		const original = error.config as (typeof error.config & { _retry?: boolean }) | undefined;
		const url = original?.url ?? "";
		const isAuthRequest = url.includes("/auth/login") || url.includes("/auth/refresh");

		if (error.response?.status === 401 && original && !original._retry && !isAuthRequest) {
			original._retry = true;
			if (!refreshPromise) {
				refreshPromise = refreshAccessToken().finally(() => {
					refreshPromise = null;
				});
			}
			const accessToken = await refreshPromise;
			if (accessToken && original.headers) {
				original.headers.Authorization = `Bearer ${accessToken}`;
				return api(original);
			}
			authStorage.clear();
			if (window.location.pathname !== "/login") window.location.href = "/login";
		}
		return Promise.reject(error);
	},
);

// API Response Types
export interface ApiResponse<T = any> {
	data: T;
	message?: string;
	status: number;
}

export interface PaginatedResponse<T> {
	data: T[];
	total: number;
	page: number;
	limit: number;
}

// Auth API
export const authAPI = {
	login: async (credentials: {
		/** Mobile number - the only sign-in identifier. */
		phone: string;
		password: string;
		rememberMe?: boolean;
		/** Disambiguates login when the same mobile exists in several companies */
		companyId?: number;
		menuSlug?: string;
	}) => {
		const response = await api.post("/auth/login", credentials);
		return response.data;
	},

	refresh: async (refreshToken: string) => {
		const response = await api.post("/auth/refresh", { refresh_token: refreshToken });
		return response.data;
	},

	logout: async (accessToken?: string | null) => {
		const response = await api.post(
			"/auth/logout",
			{},
			{ headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined },
		);
		return response.data;
	},

	profile: async () => {
		const response = await api.get("/auth/profile");
		return response.data;
	},

	updateProfile: async (data: {
		fullName?: string;
		email?: string;
		phone?: string;
		address?: string;
		birthDate?: string;
		photo?: string;
		currentPassword?: string;
		newPassword?: string;
	}) => {
		const response = await api.patch("/auth/profile", data);
		return response.data;
	},

	uploadPhoto: async (file: File) => {
		const formData = new FormData();
		formData.append("file", file);
		const response = await api.post("/auth/upload-photo", formData, {
			headers: { "Content-Type": "multipart/form-data" },
		});
		return response.data;
	},

	/** Sends a 6-digit code by SMS; `devCode` is returned only outside production (no SMS provider yet). */
	forgotPassword: async (phone: string): Promise<{ ok: true; devCode?: string }> => {
		const response = await api.post("/auth/forgot-password", { phone });
		return response.data;
	},

	/** Answers 409 `auth.errors.selectCompany` with `companies` when the code is valid in several companies. */
	resetPassword: async (body: {
		phone: string;
		code: string;
		newPassword: string;
		confirmPassword: string;
		companyId?: number;
	}): Promise<{ ok: true }> => {
		const response = await api.post("/auth/reset-password", body);
		return response.data;
	},

	unlockPosPin: async (userId: number) => {
		const response = await api.post("/auth/pos-pin-unlock", { userId });
		return response.data;
	},

	getPosPinStatus: async (userId: number): Promise<{ locked: boolean; lockedUntil: number | null }> => {
		const response = await api.get(`/auth/pos-pin-status/${userId}`);
		return response.data;
	},
};

// Users API
export const usersAPI = {
	getAll: async (params?: { page?: number; limit?: number; search?: string; branchId?: number | string }) => {
		const response = await api.get("/users", { params });
		return response.data;
	},

	getById: async (id: number | string) => {
		const response = await api.get(`/users/${id}`);
		return response.data;
	},

	create: async (userData: any) => {
		const response = await api.post("/users", userData);
		return response.data;
	},

	update: async (id: number | string, userData: any) => {
		const response = await api.patch(`/users/${id}`, userData);
		return response.data;
	},

	setPosPin: async (id: number | string, pin: string) => {
		const response = await api.patch(`/users/${id}/pos-pin`, { pin });
		return response.data;
	},

	/** When `userId` is set, uploads and persists photo on that user (tenant-scoped). Otherwise stores file only (e.g. new user create). */
	uploadPhoto: async (file: File, userId?: number | string) => {
		const formData = new FormData();
		formData.append("file", file);
		const path = userId != null && userId !== "" ? `/users/${userId}/upload-photo` : "/users/upload-photo";
		const response = await api.post(path, formData, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});
		return response.data;
	},

	delete: async (id: number | string) => {
		const response = await api.delete(`/users/${id}`);
		return response.data;
	},
};

// Products API
export const productsAPI = {
 getKitItems: async (id: number) => (await api.get(`/products/${id}/kit-items`)).data,
	getAll: async (params?: {
		page?: number;
		limit?: number;
		search?: string;
		categoryId?: number | string;
		branchId?: number | string;
		status?: string;
		type?: string;
	}) => {
		const response = await api.get("/products", { params });
		return response.data;
	},

	getById: async (id: number | string) => {
		const response = await api.get(`/products/${id}`);
		return response.data;
	},

	getCrossBranchAvailability: async (q: string) => {
		const response = await api.get("/products/cross-branch-availability", {
			params: { q },
		});
		return response.data as Array<{
			branchId: number;
			branchName: string;
			productId: number;
			name: string;
			sku: string | null;
			barcode: string | null;
			quantity: number;
			status: string;
		}>;
	},

	getBySku: async (sku: string) => {
		const response = await api.get(`/products/sku/${sku}`);
		return response.data;
	},

	getByBarcode: async (barcode: string) => {
		const response = await api.get(`/products/barcode/${barcode}`);
		return response.data;
	},

	generateBarcode: async (branchId?: number | string) => {
		const response = await api.get("/products/tools/generate-barcode", {
			params: branchId != null && branchId !== "" ? { branchId } : {},
		});
		return response.data as { barcode: string };
	},

	create: async (productData: any) => {
		const response = await api.post("/products", productData);
		return response.data;
	},

	update: async (id: number | string, productData: any) => {
		const response = await api.patch(`/products/${id}`, productData);
		return response.data;
	},

	delete: async (id: number | string) => {
		const response = await api.delete(`/products/${id}`);
		return response.data;
	},

	updateStock: async (id: number | string, quantity: number, operation: "add" | "subtract") => {
		const response = await api.patch(`/products/${id}/stock`, {
			quantity,
			operation,
		});
		return response.data;
	},

	getLowStock: async (branchId?: number | string) => {
		const response = await api.get("/products/low-stock", {
			params: { branchId },
		});
		return response.data;
	},

	getStatistics: async (branchId?: number | string) => {
		const response = await api.get("/products/statistics", {
			params: { branchId },
		});
		return response.data;
	},
};

// Recipes API
export const recipesAPI = {
	getAll: async (branchId?: number | string) => {
		const response = await api.get("/recipes", {
			params: branchId != null ? { branchId } : undefined,
		});
		return response.data;
	},

	getById: async (id: number) => {
		const response = await api.get(`/recipes/${id}`);
		return response.data;
	},

	getByProductId: async (productId: number) => {
		const response = await api.get(`/recipes/by-product/${productId}`);
		return response.data;
	},

	create: async (data: {
		menuProductId: number;
		name?: string;
		branchId?: number | null;
		isActive?: boolean;
		ingredients?: Array<{
			productId: number;
			quantity: number;
			unit: string;
			wastePercent?: number;
			isOptional?: boolean;
			sortOrder?: number;
		}>;
	}) => {
		const response = await api.post("/recipes", data);
		return response.data;
	},

	update: async (
		id: number,
		data: {
			name?: string;
			branchId?: number | null;
			isActive?: boolean;
			ingredients?: Array<{
				productId: number;
				quantity: number;
				unit: string;
				wastePercent?: number;
				isOptional?: boolean;
				sortOrder?: number;
			}>;
		},
	) => {
		const response = await api.put(`/recipes/${id}`, data);
		return response.data;
	},

	delete: async (id: number) => {
		const response = await api.delete(`/recipes/${id}`);
		return response.data;
	},
};

// Modifiers API
export const modifiersAPI = {
	getByProductId: async (productId: number) => {
		const response = await api.get(`/modifiers/product/${productId}`);
		return response.data;
	},
	createGroup: async (data: {
		name: string;
		productId: number;
		isRequired?: boolean;
		minSelections?: number;
		maxSelections?: number;
	}) => {
		const response = await api.post("/modifiers/groups", data);
		return response.data;
	},
	updateGroup: async (
		id: number,
		data: Partial<{ name: string; isRequired: boolean; minSelections: number; maxSelections: number }>,
	) => {
		const response = await api.put(`/modifiers/groups/${id}`, data);
		return response.data;
	},
	deleteGroup: async (id: number) => {
		const response = await api.delete(`/modifiers/groups/${id}`);
		return response.data;
	},
	createOption: async (data: {
		name: string;
		modifierGroupId: number;
		priceAdjustment?: number;
		ingredientProductId?: number | null;
		extraQuantity?: number | null;
		extraQuantityUnit?: string | null;
	}) => {
		const response = await api.post("/modifiers/options", data);
		return response.data;
	},
	updateOption: async (
		id: number,
		data: Partial<{
			name: string;
			priceAdjustment: number;
			ingredientProductId: number | null;
			extraQuantity: number | null;
			extraQuantityUnit: string | null;
		}>,
	) => {
		const response = await api.put(`/modifiers/options/${id}`, data);
		return response.data;
	},
	deleteOption: async (id: number) => {
		const response = await api.delete(`/modifiers/options/${id}`);
		return response.data;
	},
};

// Recipe settings API
export const recipeSettingsAPI = {
	get: async () => {
		const response = await api.get("/recipe-settings");
		return response.data;
	},
	update: async (data: { blockSaleWhenInsufficientStock?: boolean; allowNegativeStock?: boolean }) => {
		const response = await api.patch("/recipe-settings", data);
		return response.data;
	},
};

// Sales API
export const salesAPI = {
	getAll: async (params?: {
		page?: number;
		limit?: number;
		search?: string;
		branchId?: number | string;
		status?: string;
		paymentStatus?: string;
		startDate?: string;
		endDate?: string;
		groupSplits?: boolean;
	}) => {
		const response = await api.get("/sales", { params });
		return response.data;
	},

	getById: async (id: number | string) => {
		const response = await api.get(`/sales/${id}`);
		return response.data;
	},

	downloadPdf: async (id: number | string) => {
		const response = await api.get(`/sales/${id}/pdf`, { responseType: "blob" });
		return response.data as Blob;
	},

	getByReference: async (reference: string) => {
		const response = await api.get(`/sales/reference/${reference}`);
		return response.data;
	},

	create: async (saleData: any) => {
		const response = await api.post("/sales", saleData);
		return response.data;
	},

	update: async (id: number | string, saleData: any) => {
		const response = await api.patch(`/sales/${id}`, saleData);
		return response.data;
	},

	updateStatus: async (id: number | string, status: string) => {
		const response = await api.patch(`/sales/${id}/status`, { status });
		return response.data;
	},

	updatePaymentStatus: async (id: number | string, paymentStatus: string) => {
		const response = await api.patch(`/sales/${id}/payment-status`, {
			paymentStatus,
		});
		return response.data;
	},

	addPayment: async (id: number | string, paymentData: { amount: number; paymentMethod: string; notes?: string }) => {
		const response = await api.post(`/sales/${id}/payments`, paymentData);
		return response.data;
	},

	getPayments: async (id: number | string) => {
		const response = await api.get(`/sales/${id}/payments`);
		return response.data;
	},

	delete: async (id: number | string) => {
		const response = await api.delete(`/sales/${id}`);
		return response.data;
	},

	getStatistics: async (branchId?: number | string, startDate?: string, endDate?: string) => {
		const response = await api.get("/sales/statistics", {
			params: { branchId, startDate, endDate },
		});
		return response.data;
	},
};

// Purchases API
export const purchasesAPI = {
	getAll: async (params?: {
		page?: number;
		limit?: number;
		search?: string;
		supplierId?: number | string;
		status?: string;
		paymentStatus?: string;
		startDate?: string;
		endDate?: string;
	}) => {
		const response = await api.get("/purchases", { params });
		return response.data;
	},

	getById: async (id: number | string) => {
		const response = await api.get(`/purchases/${id}`);
		return response.data;
	},

	downloadPdf: async (id: number | string) => {
		const response = await api.get(`/purchases/${id}/pdf`, { responseType: "blob" });
		return response.data as Blob;
	},

	create: async (purchaseData: any) => {
		const response = await api.post("/purchases", purchaseData);
		return response.data;
	},

	update: async (id: number | string, purchaseData: any) => {
		const response = await api.patch(`/purchases/${id}`, purchaseData);
		return response.data;
	},

	updateStatus: async (id: number | string, status: string) => {
		const response = await api.patch(`/purchases/${id}/status`, { status });
		return response.data;
	},

	updatePaymentStatus: async (id: number | string, paymentStatus: string) => {
		const response = await api.patch(`/purchases/${id}/payment-status`, {
			paymentStatus,
		});
		return response.data;
	},

	delete: async (id: number | string) => {
		const response = await api.delete(`/purchases/${id}`);
		return response.data;
	},

	getStatistics: async (supplierId?: number | string, startDate?: string, endDate?: string) => {
		const response = await api.get("/purchases/statistics", {
			params: { supplierId, startDate, endDate },
		});
		return response.data;
	},
};

// Customers API
export const customersAPI = {
	getAll: async (params?: {
		page?: number;
		limit?: number;
		search?: string;
		branchId?: number | string;
		status?: string;
	}) => {
		const response = await api.get("/customers", { params });
		return response.data;
	},

	getById: async (id: number | string) => {
		const response = await api.get(`/customers/${id}`);
		return response.data;
	},

	search: async (query: string, branchId?: number | string) => {
		const response = await api.get("/customers/search", {
			params: { q: query, branchId },
		});
		return response.data;
	},

	getByPhone: async (phone: string) => {
		const response = await api.get(`/customers/phone/${phone}`);
		return response.data;
	},

	getByEmail: async (email: string) => {
		const response = await api.get(`/customers/email/${email}`);
		return response.data;
	},

	create: async (customerData: any) => {
		const response = await api.post("/customers", customerData);
		return response.data;
	},

	update: async (id: number | string, customerData: any) => {
		const response = await api.patch(`/customers/${id}`, customerData);
		return response.data;
	},

	delete: async (id: number | string) => {
		const response = await api.delete(`/customers/${id}`);
		return response.data;
	},

	getStatistics: async (branchId?: number | string) => {
		const response = await api.get("/customers/statistics", {
			params: { branchId },
		});
		return response.data;
	},
};

// Categories API
export const categoriesAPI = {
	getAll: async (params?: { page?: number; limit?: number; search?: string; status?: string }) => {
		const response = await api.get("/categories", { params });
		return response.data;
	},

	getById: async (id: number | string) => {
		const response = await api.get(`/categories/${id}`);
		return response.data;
	},

	getTree: async () => {
		const response = await api.get("/categories/tree");
		return response.data;
	},

	getRoot: async () => {
		const response = await api.get("/categories/root");
		return response.data;
	},

	create: async (categoryData: any) => {
		const response = await api.post("/categories", categoryData);
		return response.data;
	},

	update: async (id: number | string, categoryData: any) => {
		const response = await api.patch(`/categories/${id}`, categoryData);
		return response.data;
	},

	delete: async (id: number | string) => {
		const response = await api.delete(`/categories/${id}`);
		return response.data;
	},
};

// Departments API
export const departmentsAPI = {
	getAll: async () => {
		const response = await api.get("/departments");
		return response.data;
	},
	getById: async (id: number | string) => {
		const response = await api.get(`/departments/${id}`);
		return response.data;
	},
	create: async (data: any) => {
		const response = await api.post("/departments", data);
		return response.data;
	},
	update: async (id: number | string, data: any) => {
		const response = await api.patch(`/departments/${id}`, data);
		return response.data;
	},
	delete: async (id: number | string) => {
		const response = await api.delete(`/departments/${id}`);
		return response.data;
	},
};

// Permissions API
export const permissionsAPI = {
	getCatalogMeta: async (): Promise<{ catalogWritesAllowed: boolean }> => {
		const response = await api.get("/permissions/meta");
		return response.data;
	},
	getAll: async () => {
		const response = await api.get("/permissions");
		return response.data;
	},
	getById: async (id: number | string) => {
		const response = await api.get(`/permissions/${id}`);
		return response.data;
	},
	create: async (data: any) => {
		const response = await api.post("/permissions", data);
		return response.data;
	},
	update: async (id: number | string, data: any) => {
		const response = await api.patch(`/permissions/${id}`, data);
		return response.data;
	},
	delete: async (id: number | string) => {
		const response = await api.delete(`/permissions/${id}`);
		return response.data;
	},
};

// Roles API
export const rolesAPI = {
	getAll: async () => {
		const response = await api.get("/roles");
		return response.data;
	},
	getById: async (id: number | string) => {
		const response = await api.get(`/roles/${id}`);
		return response.data;
	},
	create: async (data: any) => {
		const response = await api.post("/roles", data);
		return response.data;
	},
	update: async (id: number | string, data: any) => {
		const response = await api.patch(`/roles/${id}`, data);
		return response.data;
	},
	delete: async (id: number | string) => {
		const response = await api.delete(`/roles/${id}`);
		return response.data;
	},
};

// Reports API - params object for filter support
export interface ReportFiltersParams {
	startDate?: string;
	endDate?: string;
	branchId?: string | number;
	posId?: string | number;
	createdById?: string | number;
	categoryId?: string | number;
	paymentMethod?: string;
	page?: number;
	limit?: number;
	groupBy?: "day" | "week" | "month";
}

export const reportsAPI = {
	getDashboardSummary: async (params?: ReportFiltersParams) => {
		const response = await api.get("/reports/dashboard", { params: params || {} });
		return response.data;
	},

	getSalesSummary: async (params?: ReportFiltersParams) => {
		const response = await api.get("/reports/sales", { params: params || {} });
		return response.data;
	},

	getRevenueSummary: async (params?: ReportFiltersParams) => {
		const response = await api.get("/reports/revenue", { params: params || {} });
		return response.data;
	},

	getProductSummary: async (branchId?: number | string) => {
		const response = await api.get("/reports/products", { params: { branchId } });
		return response.data;
	},

	getCustomerSummary: async (branchId?: number | string) => {
		const response = await api.get("/reports/customers", { params: { branchId } });
		return response.data;
	},

	getTopProducts: async (params?: ReportFiltersParams & { limit?: number }) => {
		const response = await api.get("/reports/top-products", { params: params || {} });
		return response.data;
	},

	getTopCustomers: async (params?: ReportFiltersParams & { limit?: number }) => {
		const response = await api.get("/reports/top-customers", { params: params || {} });
		return response.data;
	},

	getLowStockProducts: async (branchId?: number | string) => {
		const response = await api.get("/reports/low-stock", { params: { branchId } });
		return response.data;
	},

	getInventoryReport: async (branchId?: number | string) => {
		const response = await api.get("/reports/inventory", { params: { branchId } });
		return response.data;
	},

	getProfitsReport: async (startDate?: string, endDate?: string, branchId?: string) => {
		const response = await api.get("/reports/profits", {
			params: { startDate, endDate, branchId: branchId || undefined },
		});
		return response.data;
	},

	getSalesTransactions: async (params?: ReportFiltersParams) => {
		const response = await api.get("/reports/sales-transactions", { params: params || {} });
		return response.data;
	},

	getReturnsTransactions: async (params?: ReportFiltersParams) => {
		const response = await api.get("/reports/returns-transactions", { params: params || {} });
		return response.data;
	},

	getProductsReport: async (params?: ReportFiltersParams) => {
		const response = await api.get("/reports/products-sales", { params: params || {} });
		return response.data;
	},

	getCustomersReport: async (params?: ReportFiltersParams) => {
		const response = await api.get("/reports/customers-joined", { params: params || {} });
		return response.data;
	},

	getSuppliersReport: async (params?: ReportFiltersParams) => {
		const response = await api.get("/reports/suppliers-joined", { params: params || {} });
		return response.data;
	},

	getExpensesReport: async (params?: ReportFiltersParams) => {
		const response = await api.get("/reports/expenses-list", { params: params || {} });
		return response.data;
	},

	getPurchasesReport: async (params?: ReportFiltersParams) => {
		const response = await api.get("/reports/purchases-list", { params: params || {} });
		return response.data;
	},

	getTaxReport: async (params?: ReportFiltersParams) => {
		const response = await api.get("/reports/tax-report", { params: params || {} });
		return response.data;
	},

	getIngredientConsumption: async (params?: { branchId?: string; startDate?: string; endDate?: string }) => {
		const response = await api.get("/reports/ingredient-consumption", { params: params || {} });
		return response.data;
	},

	getFoodCost: async (params?: { branchId?: string; startDate?: string; endDate?: string }) => {
		const response = await api.get("/reports/food-cost", { params: params || {} });
		return response.data;
	},

	getRecipeProfitability: async (params?: { branchId?: string; startDate?: string; endDate?: string }) => {
		const response = await api.get("/reports/recipe-profitability", { params: params || {} });
		return response.data;
	},

	getSalesByDateRange: async (params?: ReportFiltersParams) => {
		const response = await api.get("/reports/sales-by-date", { params: params || {} });
		return response.data;
	},

	getBranchesReport: async (params?: ReportFiltersParams) => {
		const response = await api.get("/reports/branches", { params: params || {} });
		return response.data;
	},

	getPosSummaryReport: async (params?: ReportFiltersParams) => {
		const response = await api.get("/reports/pos-summary", { params: params || {} });
		return response.data;
	},

	getEmployeesReport: async (params?: ReportFiltersParams) => {
		const response = await api.get("/reports/employees", { params: params || {} });
		return response.data;
	},

	getCategoriesReport: async (params?: ReportFiltersParams) => {
		const response = await api.get("/reports/categories", { params: params || {} });
		return response.data;
	},

	getPaymentMethodsReport: async (params?: ReportFiltersParams) => {
		const response = await api.get("/reports/payment-methods", { params: params || {} });
		return response.data;
	},

	getDepartmentsReport: async (params?: ReportFiltersParams) => {
		const response = await api.get("/reports/departments", { params: params || {} });
		return response.data;
	},

	getRolesSummaryReport: async () => {
		const response = await api.get("/reports/roles-summary");
		return response.data;
	},

	getReturnsSummaryReport: async (params?: ReportFiltersParams) => {
		const response = await api.get("/reports/returns-summary", { params: params || {} });
		return response.data;
	},
};

// Expenses API
export const expensesAPI = {
	getAll: (
		page = 1,
		limit = 10,
		categoryId?: number | string,
		startDate?: string,
		endDate?: string,
		search?: string,
	) =>
		api
			.get("/expenses", {
				params: {
					page,
					limit,
					categoryId: categoryId || undefined,
					startDate: startDate || undefined,
					endDate: endDate || undefined,
					search: search || undefined,
				},
			})
			.then(res => res.data),
	getById: (id: number | string) => api.get(`/expenses/${id}`).then(res => res.data),
	create: (data: any) => api.post("/expenses", data).then(res => res.data),
	update: (id: number | string, data: any) => api.patch(`/expenses/${id}`, data).then(res => res.data),
	remove: (id: number | string) => api.delete(`/expenses/${id}`).then(res => res.data),
	getAllCategories: () => api.get("/expenses/categories").then(res => res.data),
	createCategory: (data: any) => api.post("/expenses/categories", data).then(res => res.data),
	updateCategory: (id: number | string, data: any) =>
		api.patch(`/expenses/categories/${id}`, data).then(res => res.data),
	removeCategory: (id: number | string) => api.delete(`/expenses/categories/${id}`).then(res => res.data),
	getStatistics: (params?: {
		categoryId?: number | string;
		startDate?: string;
		endDate?: string;
		search?: string;
	}) =>
		api.get("/expenses/statistics", { params }).then(res => res.data),
};

// Taxes API
export const taxesAPI = {
	getAll: () => api.get("/taxes").then(res => res.data),
	getById: (id: number | string) => api.get(`/taxes/${id}`).then(res => res.data),
	create: (data: any) => api.post("/taxes", data).then(res => res.data),
	update: (id: number | string, data: any) => api.patch(`/taxes/${id}`, data).then(res => res.data),
	remove: (id: number | string) => api.delete(`/taxes/${id}`).then(res => res.data),
	getStatistics: () => api.get("/taxes/statistics").then(res => res.data),
};

// Payment Methods API
export const paymentMethodsAPI = {
	getAll: () => api.get("/payment-methods").then(res => res.data),
	getById: (id: number | string) => api.get(`/payment-methods/${id}`).then(res => res.data),
	create: (data: any) => api.post("/payment-methods", data).then(res => res.data),
	update: (id: number | string, data: any) => api.patch(`/payment-methods/${id}`, data).then(res => res.data),
	remove: (id: number | string) => api.delete(`/payment-methods/${id}`).then(res => res.data),
	getStatistics: () => api.get("/payment-methods/statistics").then(res => res.data),
	getBranchAvailability: (branchId: number | string) =>
		api.get(`/payment-methods/branch/${branchId}/availability`).then(res => res.data),
	setBranchAvailability: (branchId: number | string, paymentMethodId: number, isActive: boolean) =>
		api.patch(`/payment-methods/branch/${branchId}`, { paymentMethodId, isActive }).then(res => res.data),
};

export const orderChannelsAPI = {
	getAll: (branchId?: number | string) =>
		api.get("/order-channels", { params: branchId != null ? { branchId } : {} }).then(res => res.data),
	create: (data: any) => api.post("/order-channels", data).then(res => res.data),
	update: (id: number | string, data: any) => api.patch(`/order-channels/${id}`, data).then(res => res.data),
	reorder: (branchId: number, orderedIds: number[]) =>
		api.patch("/order-channels/reorder", { branchId, orderedIds }).then(res => res.data),
	delete: (id: number | string) => api.delete(`/order-channels/${id}`).then(res => res.data),
};

// Company API
export const companyAPI = {
	getAll: () => api.get("/company").then(res => res.data),
	getById: (id: number | string) => api.get(`/company/${id}`).then(res => res.data),
	create: (data: any) => api.post("/company", data).then(res => res.data),
	update: (id: number | string, data: any) => api.patch(`/company/${id}`, data).then(res => res.data),
	remove: (id: number | string) => api.delete(`/company/${id}`).then(res => res.data),
	getProfile: () => api.get("/company/profile").then(res => res.data),
	updateProfile: (data: any) => api.patch("/company/profile", data).then(res => res.data),
	updateStorefrontPaymentMethods: (paymentMethodIds: number[]) =>
		api.patch("/company/profile/storefront-payment-methods", { paymentMethodIds }).then(res => res.data),
	updateStorefrontContent: (data: {
        appearance?: StorefrontAppearance;
		banners?: Array<{
			id: string;
			name: string;
			imageUrl: string;
			linkType: "product" | "category" | "custom";
			linkValue: string;
			startAt: string | null;
			endAt: string | null;
		}>;
		aboutText?: string;
		faqText?: string;
		termsText?: string;
		privacyText?: string;
		supportPhone?: string;
		supportEmail?: string;
		supportWhatsapp?: string;
		supportWebsite?: string;
		supportAddress?: string;
	}) => api.patch("/company/profile/storefront-content", data).then(res => res.data),
	uploadStorefrontBanner: async (file: File) => {
		const formData = new FormData();
		formData.append("file", file);
		const response = await api.post("/company/upload-storefront-banner", formData, {
			headers: { "Content-Type": "multipart/form-data" },
		});
		return response.data as { url: string; filename: string; originalName: string; size: number };
	},
	uploadLogo: async (file: File) => {
		const formData = new FormData();
		formData.append("file", file);
		const response = await api.post("/company/upload-logo", formData, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});
		return response.data;
	},
	getStatistics: () => api.get("/company/statistics").then(res => res.data),
	getSubscription: () => api.get("/company/subscription").then(res => res.data),
	updateSubscription: (
		companyId: number | string,
		data: { subscriptionPlanId: number; subscriptionStartedAt?: string; subscriptionEndsAt?: string },
	) => api.patch(`/company/${companyId}/subscription`, data).then(res => res.data),
};

// Subscriptions API (super admin only)
export const subscriptionsAPI = {
	getPlans: () => api.get("/subscriptions/plans").then(res => res.data),
	getFeatures: () => api.get("/subscriptions/features").then(res => res.data),
};

// Returns API
export const returnsAPI = {
	getAll: (page = 1, limit = 10, filters?: any) => {
		const params = { page, limit, ...filters };
		return api.get("/returns", { params }).then(res => res.data);
	},
	getById: (id: number | string) => api.get(`/returns/${id}`).then(res => res.data),
	create: (data: any) => api.post("/returns", data).then(res => res.data),
	update: (id: number | string, data: any) => api.patch(`/returns/${id}`, data).then(res => res.data),
	delete: (id: number | string) => api.delete(`/returns/${id}`).then(res => res.data),
	updateStatus: (id: number | string, status: string) =>
		api.patch(`/returns/${id}/status`, { status }).then(res => res.data),
	updatePaymentStatus: (id: number | string, paymentStatus: string, paidAmount: number) =>
		api.patch(`/returns/${id}/payment-status`, { paymentStatus, paidAmount }).then(res => res.data),
	getStatistics: (branchId?: number | string) =>
		api.get("/returns/statistics", { params: branchId != null ? { branchId } : {} }).then(res => res.data),
	downloadCreditNote: (id: number | string) =>
		api.get(`/returns/${id}/pdf`, { responseType: "blob" }).then(res => res.data as Blob),
};

// Quotations / price offers API
export const quotationsAPI = {
	getAll: (page = 1, limit = 10, filters?: { status?: string; branchId?: number | string }) => {
		const params = { page, limit, ...filters };
		return api.get("/quotations", { params }).then(res => res.data);
	},
	getById: (id: number | string) => api.get(`/quotations/${id}`).then(res => res.data),
	create: (data: any) => api.post("/quotations", data).then(res => res.data),
	update: (id: number | string, data: any) => api.patch(`/quotations/${id}`, data).then(res => res.data),
	updateStatus: (id: number | string, status: string) =>
		api.patch(`/quotations/${id}/status`, { status }).then(res => res.data),
	convert: (id: number | string) => api.post(`/quotations/${id}/convert`, {}).then(res => res.data),
	delete: (id: number | string) => api.delete(`/quotations/${id}`).then(res => res.data),
	downloadPdf: (id: number | string) =>
		api.get(`/quotations/${id}/pdf`, { responseType: "blob" }).then(res => res.data as Blob),
};

// Stock Transfers API
export const stockTransfersAPI = {
	getAll: (page = 1, limit = 10, filters?: { fromBranchId?: number; toBranchId?: number; status?: string }) => {
		const params = { page, limit, ...filters };
		return api.get("/stock-transfers", { params }).then(res => res.data);
	},
	getById: (id: number | string) => api.get(`/stock-transfers/${id}`).then(res => res.data),
	create: (data: {
		fromBranchId: number;
		toBranchId: number;
		items: { productId: number; quantity: number }[];
		notes?: string;
		asDraft?: boolean;
	}) => api.post("/stock-transfers", data).then(res => res.data),
	submit: (id: number) => api.patch(`/stock-transfers/${id}/submit`, {}).then(res => res.data),
	approve: (id: number) => api.patch(`/stock-transfers/${id}/approve`, {}).then(res => res.data),
	reject: (id: number, reason?: string) =>
		api.patch(`/stock-transfers/${id}/reject`, { reason }).then(res => res.data),
	receive: (
		id: number,
		data: { items: { stockTransferItemId: number; receivedQuantity: number; damagedQuantity?: number }[] },
	) => api.patch(`/stock-transfers/${id}/receive`, data).then(res => res.data),
	cancel: (id: number) => api.patch(`/stock-transfers/${id}/cancel`, {}).then(res => res.data),
};

// Audit API
export const auditAPI = {
	getAll: (params?: {
		page?: number;
		limit?: number;
		userId?: number;
		entityType?: string;
		action?: string;
		startDate?: string;
		endDate?: string;
	}) => {
		return api.get("/audit", { params }).then(res => res.data);
	},
};

// Shifts API (POS)
export const shiftsAPI = {
	open: (data: { branchId: number; posId?: number; openingCash?: number; openedById?: number }) =>
		api.post("/pos/shifts/open", data).then(res => res.data),
	close: (id: number, data: { manualCashCounted?: number; closingCash?: number }) =>
		api.post(`/pos/shifts/${id}/close`, data).then(res => res.data),
	getClosePreview: (id: number) => api.get(`/pos/shifts/${id}/close-preview`).then(res => res.data),
	getCashMovements: (id: number) => api.get(`/pos/shifts/${id}/cash-movements`).then(res => res.data),
	getCurrent: (params?: { branchId?: number; posId?: number }) =>
		api.get("/pos/shifts/current", { params }).then(res => res.data),
	getAll: (params?: {
		page?: number;
		limit?: number;
		branchId?: number;
		posId?: number;
		startDate?: string;
		endDate?: string;
	}) => api.get("/pos/shifts", { params }).then(res => res.data),
	getById: (id: number) => api.get(`/pos/shifts/${id}`).then(res => res.data),
};

// Suppliers API
export const suppliersAPI = {
	getAll: async (params?: any) => {
		const queryString = new URLSearchParams();
		Object.entries(params || {}).forEach(([key, value]) => {
			if (value !== undefined && value !== null) queryString.append(key, value.toString());
		});

		const response = await api.get(`/suppliers?${queryString}`);
		return response.data;
	},

	getById: async (id: number | string) => {
		const response = await api.get(`/suppliers/${id}`);
		return response.data;
	},

	create: async (data: any) => {
		const response = await api.post("/suppliers", data);
		return response.data;
	},

	update: async (id: number | string, data: any) => {
		const response = await api.patch(`/suppliers/${id}`, data);
		return response.data;
	},

	delete: async (id: number | string) => {
		const response = await api.delete(`/suppliers/${id}`);
		return response.data;
	},

	getStatistics: async () => {
		const response = await api.get("/suppliers/statistics");
		return response.data;
	},
};

// Notifications API
export const notificationsAPI = {
	getAll: (page = 1, limit = 20, filters?: any) => {
		const params = { page, limit, ...filters };
		return api.get("/notifications", { params }).then(res => res.data);
	},
	getById: (id: number | string) => api.get(`/notifications/${id}`).then(res => res.data),
	create: (data: any) => api.post("/notifications", data).then(res => res.data),
	update: (id: number | string, data: any) => api.patch(`/notifications/${id}`, data).then(res => res.data),
	delete: (id: number | string) => api.delete(`/notifications/${id}`).then(res => res.data),
	markAsRead: (id: number | string) => api.patch(`/notifications/${id}/read`).then(res => res.data),
	markAllAsRead: () => api.patch("/notifications/mark-all-read").then(res => res.data),
	markAsArchived: (id: number | string) => api.patch(`/notifications/${id}/archive`).then(res => res.data),
	getUnreadCount: () => api.get("/notifications/unread-count").then(res => res.data),
	getStatistics: () => api.get("/notifications/statistics").then(res => res.data),
	createSystem: (data: any) => api.post("/notifications/system", data).then(res => res.data),
};

// Branches API
export const branchesAPI = {
	getAll: () => api.get("/branches").then(res => res.data),
	getById: (id: number | string) => api.get(`/branches/${id}`).then(res => res.data),
	create: (data: any) => api.post("/branches", data).then(res => res.data),
	update: (id: number | string, data: any) => api.patch(`/branches/${id}`, data).then(res => res.data),
	delete: (id: number | string) => api.delete(`/branches/${id}`).then(res => res.data),
	getStatistics: () => api.get("/branches/statistics").then(res => res.data),
};

// POS API
export const posAPI = {
	getAll: async (params?: {
		page?: number;
		limit?: number;
		search?: string;
		branchId?: number | string;
		status?: string;
		dateFrom?: string;
		dateTo?: string;
	}) => {
		const response = await api.get("/pos", { params });
		return response.data;
	},

	getById: async (id: number | string) => {
		const response = await api.get(`/pos/${id}`);
		return response.data;
	},

	getStorefrontBanners: (id: number | string) => api.get(`/pos/${id}/storefront-banners`).then(res => res.data),

	updateStorefrontBanners: (
		id: number | string,
		banners: Array<{
			id: string;
			name: string;
			imageUrl: string;
			linkType: "product" | "category" | "custom";
			linkValue: string;
			startAt: string | null;
			endAt: string | null;
		}>,
	) => api.patch(`/pos/${id}/storefront-banners`, { banners }).then(res => res.data),

	create: async (data: any) => {
		const response = await api.post("/pos", data);
		return response.data;
	},

	update: async (id: number | string, data: any) => {
		const response = await api.patch(`/pos/${id}`, data);
		return response.data;
	},

	delete: async (id: number | string) => {
		const response = await api.delete(`/pos/${id}`);
		return response.data;
	},

	generateCode: async () => {
		const response = await api.post("/pos/generate-code");
		return response.data;
	},

	getStatistics: async (id: number | string) => {
		const response = await api.get(`/pos/${id}/statistics`);
		return response.data;
	},
};

// Weight Sales Configuration API
export const weightSalesConfigAPI = {
	getAll: () => api.get("/weight-sales-config").then(res => res.data),
	getById: (id: number | string) => api.get(`/weight-sales-config/${id}`).then(res => res.data),
	create: (data: any) => api.post("/weight-sales-config", data).then(res => res.data),
	update: (id: number | string, data: any) => api.patch(`/weight-sales-config/${id}`, data).then(res => res.data),
	delete: (id: number | string) => api.delete(`/weight-sales-config/${id}`).then(res => res.data),
	getActive: () => api.get("/weight-sales-config/active").then(res => res.data),
	getDefault: () => api.get("/weight-sales-config/default").then(res => res.data),
	activate: (id: number | string) => api.patch(`/weight-sales-config/${id}/activate`).then(res => res.data),
	deactivate: (id: number | string) => api.patch(`/weight-sales-config/${id}/deactivate`).then(res => res.data),
};

// Restaurant Tables API
export const tablesAPI = {
	getAll: (branchId?: number | string) =>
		api.get("/tables", { params: branchId != null ? { branchId } : {} }).then(res => res.data),
	getById: (id: number | string) => api.get(`/tables/${id}`).then(res => res.data),
	create: (data: any) => api.post("/tables", data).then(res => res.data),
	bulkCreate: (data: any) => api.post("/tables/bulk", data).then(res => res.data),
	update: (id: number | string, data: any) => api.patch(`/tables/${id}`, data).then(res => res.data),
	delete: (id: number | string) => api.delete(`/tables/${id}`).then(res => res.data),
};

export const orderTypesAPI = {
	getAll: (branchId?: number | string) =>
		api.get("/order-types", { params: branchId != null ? { branchId } : {} }).then(res => res.data),
	create: (data: any) => api.post("/order-types", data).then(res => res.data),
	update: (id: number | string, data: any) => api.patch(`/order-types/${id}`, data).then(res => res.data),
	reorder: (branchId: number, orderedIds: number[]) =>
		api.patch("/order-types/reorder", { branchId, orderedIds }).then(res => res.data),
	delete: (id: number | string) => api.delete(`/order-types/${id}`).then(res => res.data),
};

export const printerConfigAPI = {
	get: (branchId: number | string) => api.get("/printer-configs", { params: { branchId } }).then(res => res.data),
	save: (data: any) => api.put("/printer-configs", data).then(res => res.data),
	test: (branchId: number) => api.post("/printer-configs/test", { branchId }).then(res => res.data),
};

// Add-on catalog types and API
export interface Addon {
	id: number;
	code?: string | null;
	name: string;
	nameEn?: string | null;
	description: string;
	descriptionEn?: string | null;
	badge?: string | null;
	icon: string;
	status: string;
	sortOrder: number;
}

export type AddonRequestStatus = "pending" | "in_progress" | "completed";

export interface AddonRequest {
	id: number;
	name: string;
	description: string;
	status: AddonRequestStatus;
	notes?: string | null;
	createdAt: string;
	createdBy?: {
		id: number;
		firstName?: string;
		lastName?: string;
	};
}

export const addonsAPI = {
	getAll: () => api.get<Addon[]>("/addons").then(res => res.data),
};

export const addonRequestsAPI = {
	getAll: () => api.get<AddonRequest[]>("/addon-requests").then(res => res.data),
	create: (data: { name: string; description: string }) =>
		api.post<AddonRequest>("/addon-requests", data).then(res => res.data),
	delete: (id: number | string) => api.delete(`/addon-requests/${id}`).then(res => res.data),
};

// Help API
export const helpAPI = {
	getHelpContent: (lang?: string, section?: string) => {
		const q = new URLSearchParams();
		if (lang) q.set("lang", lang);
		if (section?.trim()) q.set("section", section.trim());
		const qs = q.toString();
		return api.get(`/help${qs ? `?${qs}` : ""}`).then(res => res.data);
	},
	getFaqs: (lang?: string) => api.get(`/help/faqs${lang ? `?lang=${lang}` : ""}`).then(res => res.data),
	getContactInfo: (lang?: string) => api.get(`/help/contact${lang ? `?lang=${lang}` : ""}`).then(res => res.data),
};

// Error payload for translation: returns key + optional params for t(key, params)
export const getApiErrorPayload = (error: any): { key: string; params?: Record<string, unknown> } => {
	if (error.response?.status === 403 && !error.response?.data?.message) {
		return { key: "errors.forbidden", params: {} };
	}
	const message = error.response?.data?.message;
	if (message != null) {
		if (typeof message === "object" && typeof message.key === "string") {
			return { key: message.key, params: message.params ?? {} };
		}
		if (typeof message === "string") {
			return { key: message, params: {} };
		}
		if (Array.isArray(message)) {
			// First element as key for combined message; or use unexpected
			const first = message[0];
			return { key: typeof first === "string" ? first : "errors.unexpected", params: {} };
		}
	}
	if (error.message) {
		return { key: error.message, params: {} };
	}
	return { key: "errors.unexpected", params: {} };
};

export const getApiErrorMessage = (
	error: unknown,
	t: (key: string, options?: Record<string, string>) => string,
	fallback: string,
): string => {
	const payload = getApiErrorPayload(error);
	if (!payload?.key) return fallback;
	const translated = t(payload.key, (payload.params ?? {}) as Record<string, string>);
	return translated === payload.key ? fallback : translated;
};

// Extract validation errors from NestJS response
export const extractValidationErrors = (error: any): Record<string, string> => {
	const errors: Record<string, string> = {};

	if (error.response?.data) {
		const data = error.response.data;

		if (Array.isArray(data.message)) {
			data.message.forEach((msg: string) => {
				const match = msg.match(/^(\w+)\s/);
				if (match) {
					const fieldName = match[1];
					if (!errors[fieldName]) {
						errors[fieldName] = msg;
					}
				} else {
					errors._general = errors._general ? `${errors._general}, ${msg}` : msg;
				}
			});
		} else if (typeof data.message === "string") {
			const match = data.message.match(/^(\w+)\s/);
			if (match) {
				const fieldName = match[1];
				errors[fieldName] = data.message;
			} else {
				errors._general = data.message;
			}
		} else if (typeof data === "object" && !data.message) {
			Object.keys(data).forEach(key => {
				if (key !== "statusCode" && key !== "error") {
					errors[key] = Array.isArray(data[key]) ? data[key].join(", ") : String(data[key]);
				}
			});
		}
	}

	return errors;
};

export const couponsAPI = {
	list: () => api.get("/coupons").then(res => res.data),
	create: (data: Record<string, unknown>) => api.post("/coupons", data).then(res => res.data),
	update: (id: number, data: Record<string, unknown>) => api.patch(`/coupons/${id}`, data).then(res => res.data),
	redemptions: (id: number, params?: { page?: number; limit?: number }) =>
		api.get(`/coupons/${id}/redemptions`, { params }).then(res => res.data),
};

export const giftCardsAPI = {
	list: () => api.get("/gift-cards").then(res => res.data),
	create: (data: Record<string, unknown>) => api.post("/gift-cards", data).then(res => res.data),
	getById: (id: number) => api.get(`/gift-cards/${id}`).then(res => res.data),
	update: (id: number, data: Record<string, unknown>) => api.patch(`/gift-cards/${id}`, data).then(res => res.data),
};

export const storeCreditAPI = {
	listTransactions: (params?: { page?: number; limit?: number; customerId?: number }) =>
		api.get("/store-credit/transactions", { params }).then(res => res.data),
	getBalance: (customerId: number) => api.get(`/store-credit/customers/${customerId}/balance`).then(res => res.data),
	getCustomerTransactions: (customerId: number, params?: { page?: number; limit?: number }) =>
		api.get(`/store-credit/customers/${customerId}/transactions`, { params }).then(res => res.data),
	issue: (customerId: number, data: { amount: number; notes?: string }) =>
		api.post(`/store-credit/customers/${customerId}/issue`, data).then(res => res.data),
	adjust: (customerId: number, data: { amount: number; notes?: string }) =>
		api.post(`/store-credit/customers/${customerId}/adjust`, data).then(res => res.data),
};

export const loyaltyAPI = {
	getSettings: () => api.get("/loyalty/settings").then(res => res.data),
	updateSettings: (data: Record<string, unknown>) => api.put("/loyalty/settings", data).then(res => res.data),
	getLedger: (customerId: number, params?: { page?: number; limit?: number }) =>
		api.get(`/loyalty/customers/${customerId}/ledger`, { params }).then(res => res.data),
	adjust: (customerId: number, data: { points: number; notes?: string }) =>
		api.post(`/loyalty/customers/${customerId}/adjust`, data).then(res => res.data),
};

export default api;

export const storefrontPreviewAPI = {
 catalog: async (productIds: number[], branchId?: number) => {
  const { data } = await api.post("/storefront-preview", { productIds, branchId });
  return data;
 },
};
